import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Upload,
  Button,
  Table,
  Breadcrumb,
  Tooltip,
  Space,
  Select,
  Input,
  InputNumber,
  Tag,
  Spin,
  Modal,
  DatePicker,
  Checkbox,
  Card,
  Divider,
} from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  UploadOutlined,
  CloseOutlined,
  CheckCircleFilled,
  WarningFilled,
  EditOutlined,
  CheckCircleOutlined,
  SaveOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  LinkOutlined,
  FilterOutlined,
  ReloadOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { Document, Page, pdfjs } from "react-pdf";
import "../../../styles/Main.scss";
import { useTranslation } from "react-i18next";
import {
  deleteRequest,
  getRequest,
  postRequest,
  patchRequest,
} from "../../../axios";
import {
  ShowErrorMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import { authUserData } from "../../../utils/Helper";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();
const { TextArea } = Input;
const normalizeLineItemsToRows = (items) =>
  (items || []).map((it, idx) => {
    const invoiceQty =
      Number(it?.quantity ?? it?.invoice_qty ?? it?.qty ?? 0) || 0;
    const amount = Number(it?.amount ?? 0) || 0;
    const rateFromApi = Number(it?.rate ?? 0) || 0;
    const unitFreight = Number(it?.unit_freight ?? it?.freight ?? 0) || 0;
    const unitPrice =
      rateFromApi > 0 && unitFreight > 0
        ? rateFromApi + unitFreight
        : invoiceQty > 0 && amount > 0
          ? amount / invoiceQty
          : rateFromApi > 0
            ? rateFromApi
            : 0;
    const fromApi =
      it?.current_received_qty ??
      it?.current_received ??
      it?.received_qty ??
      it?.quantity_received;
    let currentReceived = invoiceQty;
    if (fromApi !== undefined && fromApi !== null && fromApi !== "") {
      const num = Number(fromApi);
      if (!Number.isNaN(num) && num > 0) currentReceived = num;
    }
    const itemId =
      it?.id ??
      it?.draft_item_id ??
      it?.inventory_draft_item_id ??
      it?.draft_line_item_id ??
      it?.line_item_id ??
      it?.item_id ??
      it?.challan_item_id ??
      it?.challan_line_item_id ??
      null;
    return {
      key: idx + 1,
      srNo: idx + 1,
      id: itemId,
      item:
        it?.raw_item_name || it?.raw_item || it?.item || it?.item_name || "",
      aiItem:
        it?.ai_mapped_name ||
        it?.ai_item ||
        it?.ai_suggested_mapping ||
        it?.aiItem ||
        "",
      invoiceQty,
      prevReceived: Number(it?.prev_received ?? 0) || 0,
      currentReceived,
      uom: it?.uom || "PCS",
      amount,
      baseRate: rateFromApi,
      unitPrice,
      unitFreight,
      originalQty: invoiceQty, 
      taxPercent:
        it?.tax_percent != null
          ? Number(it.tax_percent)
          : it?.cgst_percent != null || it?.sgst_percent != null
            ? Number(it?.cgst_percent ?? 0) + Number(it?.sgst_percent ?? 0)
            : null, 
      cgstAmount: Number(it?.cgst_amount ?? 0) || 0,
      originalCgstAmount: Number(it?.cgst_amount ?? 0) || 0,
      sgstAmount: Number(it?.sgst_amount ?? 0) || 0,
      originalSgstAmount: Number(it?.sgst_amount ?? 0) || 0, 
      totalAmount: Number(it?.total_amount ?? 0) || 0,
      confirmed: Boolean(it?.confirmed),
      confidenceScore: Number(it?.confidence_score ?? 0.85) || 0.85,
    };
  });

const formatShippingAddressForMatch = (addr) => {
  if (!addr) return "";
  const parts = [
    addr.shipping_address_line_1,
    addr.shipping_address_line_2,
    addr.shipping_city,
    addr.shipping_state,
    addr.shipping_pincode_number != null && addr.shipping_pincode_number !== ""
      ? String(addr.shipping_pincode_number)
      : addr.shipping_pincode != null
        ? String(addr.shipping_pincode)
        : null,
  ].filter(Boolean);
  return parts.join(", ");
};

const normalizeAddressForMatch = (str) => {
  if (str == null || typeof str !== "string") return "";
  return str.toLowerCase().trim().replace(/\s+/g, " ");
};

const AiDocIntakePdf = ({
  embedded,
  editDraftId: editDraftIdProp,
  onSaveAsDraft,
  onConfirmReceipt,
} = {}) => {
  const navigate = useNavigate();
  const { id: draftIdFromParams } = useParams();
  const editDraftIdSource =
    embedded && editDraftIdProp != null
      ? String(editDraftIdProp)
      : draftIdFromParams;
  const { t } = useTranslation();
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFileUrl, setPdfFileUrl] = useState(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [invoiceIntakeResult, setInvoiceIntakeResult] = useState(null);

  const [dataSource, setDataSource] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [draftData, setDraftData] = useState(null);
  const [currentInvoiceId, setCurrentInvoiceId] = useState(null);
  const [currentInvoicePk, setCurrentInvoicePk] = useState(null);

  const CompanyId = authUserData()?.company || null;
  const [supplierList, setSupplierList] = useState([]);
  const [siteLocationList, setSiteLocationList] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [userDetails, setUserDetails] = useState([]);
  const [documentType, setDocumentType] = useState(null);
  const [showLinkChallansModal, setShowLinkChallansModal] = useState(false);
  const [linkChallansMode, setLinkChallansMode] = useState("link"); 
  const [hasInitializedChallansFromEditApi, setHasInitializedChallansFromEditApi] =
    useState(false);
  const [linkInvoiceVendorId, setLinkInvoiceVendorId] = useState(null);
  const [linkInvoiceNumber, setLinkInvoiceNumber] = useState("");
  const [linkInvoiceDate, setLinkInvoiceDate] = useState(null);
  const [linkInvoiceAmount, setLinkInvoiceAmount] = useState(null);
  const [availableChallans, setAvailableChallans] = useState([]);
  const [selectedChallanIds, setSelectedChallanIds] = useState([]);
  const [loadingChallans, setLoadingChallans] = useState(false);
  const [challanSearchText, setChallanSearchText] = useState("");
  const [debouncedChallanSearchText, setDebouncedChallanSearchText] =
    useState("");
  const [challanDateRange, setChallanDateRange] = useState(null);
  const [linkingChallans, setLinkingChallans] = useState(false);
  const [hasLinkedChallans, setHasLinkedChallans] = useState(false);
  const [loadingLinkedChallans, setLoadingLinkedChallans] = useState(false);

  const isPdfFile = (file) =>
    !!file &&
    (file.type === "application/pdf" ||
      (typeof file.name === "string" &&
        file.name.toLowerCase().endsWith(".pdf")));

  const isImageFile = (file) =>
    !!file && typeof file.type === "string" && file.type.startsWith("image/");

  const isPdfUrl = (url) =>
    typeof url === "string" &&
    (url.toLowerCase().includes(".pdf") ||
      url.toLowerCase().includes("application/pdf"));

  useEffect(() => {
    if (!pdfFile || (!isImageFile(pdfFile) && !isPdfFile(pdfFile))) {
      setLocalPreviewUrl(null);
      return;
    }
    const objUrl = URL.createObjectURL(pdfFile);
    setLocalPreviewUrl(objUrl);
    return () => URL.revokeObjectURL(objUrl);
  }, [pdfFile]);

  const addUserDetailRow = () => {
    setUserDetails((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random()}`, name: "", email: "", mobile: "" },
    ]);
  };
  const removeUserDetailRow = (id) => {
    setUserDetails((prev) => prev.filter((r) => r.id !== id));
  };
  const updateUserDetailRow = (id, field, value) => {
    setUserDetails((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  const UOM_OPTIONS = [
    { label: "PCS", value: "PCS" },
    { label: "NOS", value: "NOS" },
  ];

  useEffect(() => {
    if (!CompanyId) return;
    const fetchSites = async () => {
      try {
        const res = await getRequest(
          `delivery-address/${CompanyId}/get-address-by-company/`,
        );
        setSiteLocationList(res?.data?.results?.data || []);
      } catch (err) {
        console.error("Failed to fetch sites:", err);
      }
    };
    fetchSites();
  }, [CompanyId]);

  useEffect(() => {
    if (!showLinkChallansModal) return;
    const fetchVendors = async () => {
      try {
        const res = await getRequest("vendors/", { page: 1, page_size: 500 });
        const payload = res?.data;
        const raw =
          payload?.results?.data ??
          payload?.results ??
          payload?.data ??
          payload;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.results)
            ? raw.results
            : Array.isArray(raw?.data)
              ? raw.data
              : [];
        setSupplierList(list);
      } catch (err) {
        console.error("Failed to fetch vendors:", err);
        ShowErrorMsg("Failed to load vendors");
      }
    };
    fetchVendors();
  }, [showLinkChallansModal]);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedChallanSearchText((challanSearchText || "").trim());
    }, 600);
    return () => clearTimeout(id);
  }, [challanSearchText]);

  const fetchChallans = async ({ reset = false, vendorOverride } = {}) => {
    if (
      linkChallansMode === "edit" &&
      !hasInitializedChallansFromEditApi &&
      vendorOverride == null
    )
      return;
    const vendorToUse = vendorOverride ?? linkInvoiceVendorId;
    if (!vendorToUse || !showLinkChallansModal) {
      setAvailableChallans([]);
      return;
    }

    setLoadingChallans(true);
    try {
      const dateRange =
        reset || !challanDateRange || challanDateRange.length !== 2
          ? null
          : challanDateRange;
      const start =
        dateRange && dateRange[0]
          ? typeof dateRange[0]?.format === "function"
            ? dateRange[0].format("YYYY-MM-DD")
            : String(dateRange[0]).slice(0, 10)
          : null;
      const end =
        dateRange && dateRange[1]
          ? typeof dateRange[1]?.format === "function"
            ? dateRange[1].format("YYYY-MM-DD")
            : String(dateRange[1]).slice(0, 10)
          : null;

      const search = reset ? "" : (debouncedChallanSearchText || "").trim();

      const params = {
        vendor: vendorToUse,
        not_linked_to_invoice: true,
        ...(start ? { start_date: start } : {}),
        ...(end ? { end_date: end } : {}),
        ...(search ? { search } : {}),
      };

      const res = await getRequest("challans/", params);
      const payload = res?.data;
      const challans =
        payload?.results?.data || payload?.results || payload?.data || [];
      setAvailableChallans(Array.isArray(challans) ? challans : []);
    } catch (err) {
      console.error("Failed to fetch challans:", err);
      ShowErrorMsg("Failed to load challans");
      setAvailableChallans([]);
    } finally {
      setLoadingChallans(false);
    }
  };

  useEffect(() => {
    if (linkChallansMode === "edit") return;
    fetchChallans();
  }, [
    linkInvoiceVendorId,
    showLinkChallansModal,
    debouncedChallanSearchText,
    challanDateRange,
    linkChallansMode,
  ]);

  const handleApplyChallanFilters = () => {
    setDebouncedChallanSearchText((challanSearchText || "").trim());
    fetchChallans();
  };

  const handleResetChallanFilters = () => {
    setChallanSearchText("");
    setChallanDateRange(null);
    fetchChallans({ reset: true });
  };

  useEffect(() => {
    const shippedTo = draftData?.shipped_to_address;
    if (!shippedTo || !siteLocationList?.length) return;

    const normalizedDraft = normalizeAddressForMatch(shippedTo);
    if (!normalizedDraft) return;
    const matched = siteLocationList.find((s) => {
      const addr = formatShippingAddressForMatch(s);
      return normalizeAddressForMatch(addr) === normalizedDraft;
    });
    if (matched) setSelectedSiteId(matched.id);
  }, [draftData?.shipped_to_address, draftData?.site_id, siteLocationList]);

  useEffect(() => {
    if (!editDraftIdSource) return;
    const loadDraftById = async () => {
      try {
        setIsUploading(true);
        const res = await getRequest(`inventory-drafts/${editDraftIdSource}/`);
        const raw = res?.data;
        const draft =
          raw?.success === true && raw?.data != null
            ? raw.data
            : raw?.data?.success === true && raw?.data?.data != null
              ? raw.data.data
              : (raw?.data ?? raw?.results?.data ?? raw);
        if (!draft) {
          ShowErrorMsg("Draft not found");
          return;
        }
        setDraftId(draft.id ?? Number(editDraftIdSource));
        setDraftData(draft);
        setCurrentInvoiceId(draft.invoice_id ?? null);
        setSelectedVendorId(draft.vendor_id ?? null);
        setSelectedSiteId(draft.site_id ?? null);
        if (draft.document_file_url) setPdfFileUrl(draft.document_file_url);
        const rawUserDetails = draft?.user_details ?? [];
        if (Array.isArray(rawUserDetails) && rawUserDetails.length > 0) {
          setUserDetails(
            rawUserDetails.map((u, idx) => ({
              id: `edit-${draft.id}-${idx}-${u?.name ?? idx}`,
              name: u?.name ?? "",
              email: u?.email ?? "",
              mobile: u?.mobile ?? "",
            })),
          );
        } else {
          setUserDetails([]);
        }
        
        const draftItems = draft?.items || draft?.line_items || draft.challan_items || [];

        if (Array.isArray(draftItems) && draftItems.length > 0) {
          setDataSource(normalizeLineItemsToRows(draftItems));
        }
      } catch (err) {
        ShowErrorMsg(err);
      } finally {
        setIsUploading(false);
      }
    };
    loadDraftById();
  }, [editDraftIdSource]);

  const performUpload = async (file, docType) => {
    setPdfFile(file);
    setPdfFileUrl(null);
    setDataSource([]);
    setInvoiceIntakeResult(null);
    setDraftId(null);
    setDraftData(null);
    setCurrentInvoiceId(null);
    setCurrentInvoicePk(null);
    setSelectedVendorId(null);
    setSelectedSiteId(null);
    setDocumentType(docType);

    const formData = new FormData();
    formData.append("file", file);
    if (docType) {
      formData.append("document_type", docType);
    }

    try {
      setIsUploading(true);
      const res = await postRequest("inventory-invoices/", formData);
      const payload = res?.data;
      setInvoiceIntakeResult(payload);
      const statusText =
        payload?.data?.status ||
        payload?.status ||
        `${docType === "challan" ? "Challan" : "Invoice"} uploaded`;
      const itemsFound = payload?.data?.items_found ?? payload?.items_found;
      const invoiceId = payload?.data?.invoice_id ?? payload?.invoice_id;
      const invoicePk = payload?.data?.id ?? payload?.id ?? null;
      setCurrentInvoiceId(invoiceId);
      setCurrentInvoicePk(invoicePk);

      SuccessNotificationMsg(
        statusText,
        [
          typeof itemsFound === "number" ? `Items found: ${itemsFound}` : null,
          invoiceId ? `Invoice ID: ${invoiceId}` : null,
          invoicePk ? `ID: ${invoicePk}` : null,
        ]
          .filter(Boolean)
          .join(" • "),
      );

      const apiItems =
        payload?.data?.items ||
        payload?.data?.line_items ||
        payload?.items ||
        payload?.line_items;
      const invoiceIdParam = invoiceId ?? invoicePk ?? null;
      const challanIdParam =
        payload?.data?.challan_id ||
        payload?.challan_id ||
        (docType === "challan" ? invoicePk ?? invoiceId ?? null : null);

      if (docType === "challan" ? !!challanIdParam : !!invoiceIdParam) {
        try {
          const draftQueryParams =
            docType === "challan"
              ? { challan_id: challanIdParam }
              : { invoice_id: invoiceIdParam };
          const draftRes = await getRequest(
            "inventory-drafts/",
            draftQueryParams,
          );
          const draftPayload = draftRes?.data;
          const draftDataArray = Array.isArray(draftPayload?.results)
            ? draftPayload.results
            : draftPayload?.results?.data || draftPayload?.data || [];
          const draft = Array.isArray(draftDataArray)
            ? draftDataArray[0]
            : draftDataArray;
          if (draft?.id) {
            setDraftId(draft.id);
          }
          if (draft) {
            setDraftData(draft);
            if (draft.vendor_id != null) {
              setSelectedVendorId(draft.vendor_id);
            }
            if (draft.site_id != null) {
              setSelectedSiteId(draft.site_id);
            }
          }

          const draftItems = draft?.items || draft?.line_items || draft.challan_items || [];
          if (Array.isArray(draftItems) && draftItems.length > 0) {
            setDataSource(normalizeLineItemsToRows(draftItems));
          } else if (Array.isArray(apiItems) && apiItems.length > 0) {
            setDataSource(normalizeLineItemsToRows(apiItems));
          }
        } catch (err) {
          if (Array.isArray(apiItems) && apiItems.length > 0) {
            setDataSource(normalizeLineItemsToRows(apiItems));
          }
          ShowErrorMsg(err);
        }
      } else if (Array.isArray(apiItems) && apiItems.length > 0) {
        setDataSource(normalizeLineItemsToRows(apiItems));
      }
    } catch (err) {
      ShowErrorMsg(err);
    } finally {
      setIsUploading(false);
    }
    return false;
  };

  const handleUpload = (file, docType) => {
    performUpload(file, docType);
    return false;
  };
  const handleOpenLinkChallansModal = async ({ mode } = { mode: "link" }) => {
    setShowLinkChallansModal(true);
    setLinkChallansMode(mode);
    setHasInitializedChallansFromEditApi(false);
    setLinkInvoiceVendorId(null);
    setLinkInvoiceNumber("");
    setLinkInvoiceDate(null);
    setLinkInvoiceAmount(null);
    setSelectedChallanIds([]);
    setChallanSearchText("");
    setChallanDateRange(null);

    if (mode === "edit") {
    const invoiceUuid = draftData?.invoice_id || currentInvoiceId || null;
    const documentId = currentInvoicePk || null;
    if (!invoiceUuid && !documentId) return;

    try {
      setLoadingLinkedChallans(true);
      const res = invoiceUuid
        ? await getRequest("invoice-challan-map/document/", {
            invoice_id: invoiceUuid,
          })
        : await getRequest(`invoice-challan-map/document/${documentId}/`);
      const payload = res?.data;
        const results = payload?.data ?? payload?.results ?? payload;
        const vendorName = results?.vendor ?? null;
        const invDate =
          results?.invoice_date ??
          results?.date ??
          results?.document_date ??
          null;
        const challansArray = Array.isArray(results?.challans)
          ? results.challans
          : Array.isArray(results?.data)
            ? results.data
            : [];

        const normalizedChallans = challansArray.map((c) => ({
          ...c,
          id: c?.id ?? c?.challan_id ?? c?.challanId,
        }));

        const checkedChallansFromApi = normalizedChallans.filter(
          (c) =>
            c?.is_checked === true ||
            c?.is_checked === "true" ||
            c?.is_checked === 1 ||
            c?.is_checked === "1" ||
            c?.is_selected === true ||
            c?.is_selected === "true" ||
            c?.is_selected === 1 ||
            c?.is_selected === "1",
        );

        const challanIds = checkedChallansFromApi
          .map((c) => c?.id)
          .filter(Boolean);
        if (vendorName) setLinkInvoiceVendorId(String(vendorName));
        else {
          const fallbackVendor =
            draftData?.vendor_name || draftData?.vendor_details?.name || null;
          if (fallbackVendor) setLinkInvoiceVendorId(String(fallbackVendor));
        }

        if (invDate) {
          const s = String(invDate).slice(0, 10);
          setLinkInvoiceDate(/^\d{4}-\d{2}-\d{2}$/.test(s) ? s : invDate);
        }

        setAvailableChallans(normalizedChallans);
        setSelectedChallanIds(challanIds);
        setHasLinkedChallans(challanIds.length > 0);
      setHasInitializedChallansFromEditApi(true);
    } catch (err) {
      ShowErrorMsg(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to load linked challans",
      );
    } finally {
      setLoadingLinkedChallans(false);
      setHasInitializedChallansFromEditApi(true);
    }
    } else {
      const vName =
        draftData?.vendor_name ||
        draftData?.vendor_details?.name ||
        null;
      if (vName) setLinkInvoiceVendorId(String(vName));
      if (draftData?.invoice_number) {
        setLinkInvoiceNumber(String(draftData.invoice_number));
      }
      const dRaw =
        draftData?.invoice_date ||
        draftData?.date ||
        draftData?.document_date ||
        draftData?.bill_date ||
        null;
      if (dRaw) {
        const s = String(dRaw).slice(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) setLinkInvoiceDate(s);
      }
    }
  };

  const handleCloseLinkChallansModal = () => {
    setShowLinkChallansModal(false);
    setLinkChallansMode("link");
    setHasInitializedChallansFromEditApi(false);
    setLinkInvoiceVendorId(null);
    setLinkInvoiceNumber("");
    setLinkInvoiceDate(null);
    setLinkInvoiceAmount(null);
    setSelectedChallanIds([]);
    setAvailableChallans([]);
    setChallanSearchText("");
    setChallanDateRange(null);
  };

  const handleVendorChange = (vendorId) => {
    setLinkInvoiceVendorId(vendorId);
    setSelectedChallanIds([]);
    if (linkChallansMode === "edit") {
      fetchChallans({ vendorOverride: vendorId });
    }
  };

  const handleChallanSelection = (checked, challanId) => {
    if (checked) {
      setSelectedChallanIds((prev) =>
        prev.includes(challanId) ? prev : [...prev, challanId],
      );
    } else {
      setSelectedChallanIds((prev) => prev.filter((id) => id !== challanId));
    }
  };

  const handleSelectAllChallans = (checked) => {
    if (checked) {
      const filteredChallans = getFilteredChallans();
      setSelectedChallanIds(filteredChallans.map((c) => c.id));
    } else {
      setSelectedChallanIds([]);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getFilteredChallans = () => {
    let filtered = [...availableChallans];
    if (challanSearchText) {
      const searchLower = challanSearchText.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          String(c.challan_number || "")
            .toLowerCase()
            .includes(searchLower) ||
          String(c.gr_id || c.gr_id || "")
            .toLowerCase()
            .includes(searchLower),
      );
    }
    if (challanDateRange && challanDateRange.length === 2) {
      const [startDate, endDate] = challanDateRange;
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter((c) => {
        const challanDate = c.date || c.challan_date || c.created_at;
        if (!challanDate) return false;
        const date = new Date(challanDate);
        return date >= start && date <= end;
      });
    }

    return filtered;
  };

  const getSelectedChallansTotal = () => {
    const selectedChallans = availableChallans.filter((c) =>
      selectedChallanIds.includes(c.id),
    );
    return selectedChallans.reduce(
      (sum, c) =>
        sum + (Number(c.total_value ?? c.total_amount ?? c.amount ?? 0) || 0),
      0,
    );
  };

  const handleLinkChallans = async () => {
    if (!currentInvoicePk && !currentInvoiceId) {
      ShowErrorMsg("Invoice ID not found. Please upload an invoice first.");
      return;
    }

    if (selectedChallanIds.length === 0) {
      ShowErrorMsg("Please select at least one challan to link.");
      return;
    }

    const selectedChallans = availableChallans.filter((c) =>
      selectedChallanIds.includes(c.id),
    );
    const selectedChallanNumbers = selectedChallans
      .map((c) => c.challan_number)
      .filter(Boolean);
    const selectedChallanTotalValue = selectedChallans.reduce((sum, c) => {
      const v = Number(c.total_value ?? c.total_amount ?? c.amount ?? 0);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
    const formatAmount = (value) => {
      const n = Number(value);
      if (!Number.isFinite(n)) return "-";
      try {
        return new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          maximumFractionDigits: 2,
        }).format(n);
      } catch {
        return String(n);
      }
    };

    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        title: "Link Challan(s) to Invoice",
        width: 620,
        centered: true,
        icon: null,
        content: (
          <div style={{ lineHeight: 1.4 }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                padding: 12,
                borderRadius: 12,
                border: "1px solid rgba(9, 78, 143, 0.18)",
                background:
                  "linear-gradient(135deg, rgba(9, 78, 143, 0.08) 0%, rgba(255, 255, 255, 1) 60%)",
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  background: "#094E8F",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <LinkOutlined style={{ color: "#fff", fontSize: 16 }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>
                  Confirm linking challans to invoice
                </div>
                <div style={{ color: "rgba(0,0,0,0.6)" }}>
                  You are about to link{" "}
                  <strong>{selectedChallanIds.length}</strong> challan(s)
                  {selectedChallanTotalValue > 0 ? (
                    <>
                      {" "}
                      totaling{" "}
                      <strong>{formatAmount(selectedChallanTotalValue)}</strong>
                    </>
                  ) : null}
                  .
                </div>
              </div>
            </div>

            <Card
              size="small"
              style={{
                borderRadius: 10,
                border: "1px solid #f0f0f0",
                background: "#fff",
                marginBottom: 10,
              }}
              bodyStyle={{ padding: 12 }}
            >
              <Row gutter={[12, 8]}>
                <Col xs={24} sm={12}>
                  <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
                    Invoice No
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {linkInvoiceNumber || draftData?.invoice_number || "—"}
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
                    Challans selected
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {selectedChallanIds.length}
                    {selectedChallanTotalValue > 0 ? (
                      <span
                        style={{ fontWeight: 500, color: "rgba(0,0,0,0.55)" }}
                      >
                        {" "}
                        • {formatAmount(selectedChallanTotalValue)}
                      </span>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </Card>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 8,
              }}
            >
              <div style={{ fontWeight: 600 }}>Selected challans</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {selectedChallanTotalValue > 0 ? (
                  <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
                    {formatAmount(selectedChallanTotalValue)}
                  </Tag>
                ) : null}
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                  {selectedChallanIds.length}
                </Tag>
              </div>
            </div>

            <div
              style={{
                maxHeight: 220,
                overflow: "auto",
                padding: 10,
                border: "1px solid #f0f0f0",
                borderRadius: 12,
                background:
                  "linear-gradient(180deg, rgba(250,250,250,1) 0%, rgba(255,255,255,1) 100%)",
              }}
            >
              {selectedChallans.length > 0 ? (
                <div style={{ display: "grid", gap: 8 }}>
                  {selectedChallans.map((c) => {
                    const challanNo = c.challan_number || `#${c.id}`;
                    const challanDate =
                      c.challan_date || c.date || c.created_at;
                    const amount = Number(
                      c.total_value ?? c.total_amount ?? c.amount ?? 0,
                    );
                    const status = c.status_display || c.status;
                    return (
                      <div
                        key={c.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          padding: "10px 10px",
                          borderRadius: 10,
                          border: "1px solid #f0f0f0",
                          background: "#fff",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={challanNo}
                          >
                            {challanNo}
                          </div>
                          <div
                            style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}
                          >
                            {challanDate
                              ? String(challanDate).slice(0, 10)
                              : "—"}
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexShrink: 0,
                          }}
                        >
                          {Number.isFinite(amount) && amount > 0 ? (
                            <Tag
                              color="geekblue"
                              style={{ marginInlineEnd: 0 }}
                            >
                              {formatAmount(amount)}
                            </Tag>
                          ) : null}
                          {status ? (
                            <Tag style={{ marginInlineEnd: 0 }}>{status}</Tag>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: "rgba(0,0,0,0.45)" }}>
                  No challans selected.
                </div>
              )}
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <div style={{ color: "rgba(0,0,0,0.65)" }}>
              This action will map the selected challan(s) to the invoice.
            </div>
          </div>
        ),
        okText: "Yes, Link",
        cancelText: "Cancel",
        okButtonProps: {
          style: {
            borderRadius: 8,
            background: "#094E8F",
            borderColor: "#094E8F",
          },
        },
        cancelButtonProps: { style: { borderRadius: 8 } },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) return;

    const documentIdNum = Number(currentInvoicePk || currentInvoiceId);
    if (!Number.isFinite(documentIdNum)) {
      ShowErrorMsg("Invalid document id for linking.");
      return;
    }

    const vendorForMap =
      (linkInvoiceVendorId && String(linkInvoiceVendorId).trim()) ||
      draftData?.vendor_name ||
      draftData?.vendor_details?.name ||
      "";
    if (!vendorForMap) {
      ShowErrorMsg("Please select a vendor before linking challans.");
      return;
    }

    const resolveInvoiceDateIso = () => {
      const intake = invoiceIntakeResult?.data ?? invoiceIntakeResult;
      const raw =
        linkInvoiceDate ||
        draftData?.invoice_date ||
        draftData?.date ||
        draftData?.document_date ||
        draftData?.bill_date ||
        intake?.invoice_date ||
        intake?.date ||
        intake?.document_date ||
        null;
      if (raw == null || raw === "") return null;
      if (typeof raw?.format === "function") {
        return raw.format("YYYY-MM-DD");
      }
      const s = String(raw).trim();
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
    };

    const invoiceDateIso = resolveInvoiceDateIso();
    if (!invoiceDateIso) {
      ShowErrorMsg(
        "Invoice date is required to link challans. Ensure the draft includes a date or set it on the invoice.",
      );
      return;
    }

    const challanIdsNumeric = selectedChallanIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));

    try {
      setLinkingChallans(true);
      const payload = {
        invoice_id: draftData?.invoice_id || documentIdNum,
        vendor: vendorForMap,
        invoice_date: invoiceDateIso,
        challan_ids: challanIdsNumeric,
      };
      if (linkChallansMode === "edit") {
        await patchRequest(
          `invoice-challan-map/update-document/`,
          {
            invoice_id: draftData?.invoice_id || documentIdNum,
            vendor: vendorForMap,
            invoice_date: invoiceDateIso,
            selected_challan_ids: challanIdsNumeric,
          },
        );
      } else {
        await postRequest("invoice-challan-map/", payload);
      }
      SuccessNotificationMsg(
        `Successfully linked ${selectedChallanIds.length} challan(s) to invoice`,
      );
      setHasLinkedChallans(true);
      handleCloseLinkChallansModal();

      // Clear selections
      setSelectedChallanIds([]);
    } catch (err) {
      ShowErrorMsg(
        err?.response?.data?.error || err?.message || "Failed to link challans",
      );
    } finally {
      setLinkingChallans(false);
    }
  };

  const clearInvoiceState = () => {
    setPdfFile(null);
    setPdfFileUrl(null);
    setNumPages(null);
    setDataSource([]);
    setInvoiceIntakeResult(null);
    setDraftId(null);
    setDraftData(null);
    setCurrentInvoiceId(null);
    setCurrentInvoicePk(null);
    setSelectedVendorId(null);
    setSelectedSiteId(null);
    setHasLinkedChallans(false);
  };

  const handleCloseInvoice = async () => {
    if (!currentInvoicePk) {
      clearInvoiceState();
      return;
    }

    try {
      setIsUploading(true);
      const delRes = await deleteRequest(
        `inventory-invoices/${currentInvoicePk}/`,
      );
      const msg =
        delRes?.data?.message ||
        delRes?.data?.results?.message ||
        delRes?.data?.detail ||
        "Invoice deleted successfully";
      SuccessNotificationMsg(msg);
    } catch (err) {
      ShowErrorMsg(err);
    } finally {
      setIsUploading(false);
      clearInvoiceState();
    }
  };

  const handleUomChange = (key, value) => {
    setDataSource((prev) =>
      prev.map((row) => (row.key === key ? { ...row, uom: value } : row)),
    );
  };

  const handleInvoiceQtyChange = (key, value) => {
    const num = Number(value);
    const qty = Number.isFinite(num) && num >= 0 ? num : 0;
    setDataSource((prev) =>
      prev.map((row) => (row.key === key ? { ...row, invoiceQty: qty } : row)),
    );
  };

  const handleUnitRateChange = (key, value) => {
    const num = Number(value);
    const rate = Number.isFinite(num) && num >= 0 ? num : 0;
    setDataSource((prev) =>
      prev.map((row) =>
        row.key === key
          ? {
              ...row,
              unitPrice: rate,
              baseRate: rate,
            }
          : row,
      ),
    );
  };

  const handleGstPercentChange = (key, value) => {
    const num = Number(value);
    const gst = Number.isFinite(num) && num >= 0 ? num : null;
    setDataSource((prev) =>
      prev.map((row) => (row.key === key ? { ...row, taxPercent: gst } : row)),
    );
  };

  const [deletingKey, setDeletingKey] = useState(null);

  const addRow = () => {
    const numericKeys = dataSource
      .map((r) => Number(r.key))
      .filter(Number.isFinite);
    const nextKey =
      numericKeys.length > 0 ? Math.max(...numericKeys, 0) + 1 : 1;
    setDataSource((prev) => [
      ...prev,
      {
        key: nextKey,
        srNo: prev.length + 1,
        id: null,
        item: "",
        aiItem: "",
        invoiceQty: 0,
        prevReceived: 0,
        currentReceived: 0,
        uom: "PCS",
        amount: 0,
        baseRate: 0,
        unitPrice: 0,
        unitFreight: 0,
        originalQty: 0,
        taxPercent: null,
        cgstAmount: 0,
        originalCgstAmount: 0,
        sgstAmount: 0,
        originalSgstAmount: 0,
        totalAmount: 0,
        confirmed: false,
        confidenceScore: 0.85,
      },
    ]);
  };

  const removeRow = async (key) => {
    const row = dataSource.find((r) => r.key === key);

    if (!row) return;
    const isChallanDoc =
      documentType === "challan" || draftData?.is_challan === true;
    const persistedRowId =
      row.id ??
      row.draft_item_id ??
      row.inventory_draft_item_id ??
      row.draft_line_item_id ??
      row.line_item_id ??
      row.item_id ??
      row.challan_item_id ??
      row.challan_line_item_id ??
      null;

    if (persistedRowId != null && persistedRowId !== "") {
      try {
        setDeletingKey(key);
        if (isChallanDoc) {
          
          const challanIdForDelete =
            draftData?.id ??
            draftData?.challan_id ??
            draftData?.challan ??
            currentInvoiceId ??
            draftData?.invoice_id ??
            null;
          await deleteRequest(`inventory-drafts/${persistedRowId}/`);
        }
        setDataSource((prev) => prev.filter((r) => r.key !== key));
      } catch (err) {
        ShowErrorMsg(
          err?.response?.data?.error || err?.message || "Failed to delete item",
        );
      } finally {
        setDeletingKey(null);
      }
    } else {
      setDataSource((prev) => prev.filter((r) => r.key !== key));
    }
  };

  const poHeader = {
    title: "Purchase Order",
    supplier: "",
    docNo: "",
    site: "",
  };

  const handleConfirm = (key) => {
    setDataSource((prev) =>
      prev.map((row) => (row.key === key ? { ...row, confirmed: true } : row)),
    );
  };

  const onQtyChange = (value, record) => {
    setDataSource((prev) =>
      prev.map((row) =>
        row.key === record.key ? { ...row, currentReceived: value || 0 } : row,
      ),
    );
  };

  const getStatus = (row) => {
    const current = row.currentReceived ?? 0;
    if (current === 0 || current === null || current === undefined)
      return "NIL";
    const totalReceived = row.prevReceived + row.currentReceived;
    if (totalReceived === row.invoiceQty) return "Fully Received";
    if (totalReceived < row.invoiceQty) return "Partially Received";
    return "Over Received";
  };
  const isChallanUpload =
    documentType === "challan" || draftData?.is_challan === true;
  const columns = [
    {
      title: "Sr.",
      key: "srNo",
      width: 50,
      align: "center",
      render: (_, __, index) => <div style={{ marginTop: 8 }}>{index + 1}</div>,
    },
    {
      title: "Raw Item Name (PDF)",
      dataIndex: "item",
      width: 180,
      ellipsis: false,
      render: (value, record) => (
        <TextArea
          type="text"
          value={value}
          autoSize={{ minRows: 1, maxRows: 8 }}
          placeholder="Item name"
          style={{ width: "100%", maxWidth: "100%" }}
          onChange={(e) => {
            const val = e.target.value;
            setDataSource((prev) =>
              prev.map((row) =>
                row.key === record.key ? { ...row, item: val } : row,
              ),
            );
          }}
        />
      ),
    },
    {
      title: "AI Suggested Mapping Item",
      dataIndex: "aiItem",
      // width: 180,
      ellipsis: false,
      render: (value) => (
        <div
          style={{
            wordBreak: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "normal",
            maxWidth: "100%",
            lineHeight: 1.4,
            marginTop: 8,
          }}
        >
          {value || "—"}
        </div>
      ),
    },
    {
      title: "Invoice Qty.",
      dataIndex: "invoiceQty",
      // width: 100,
      render: (value, record) => {
        if (record.isStatusRow) return value;
        return (
          <InputNumber
            min={0}
            controls={false}
            value={value}
            style={{ width: "100%" }}
            onChange={(v) => handleInvoiceQtyChange(record.key, v)}
          />
        );
      },
    },
    ...(!isChallanUpload
      ? [
          {
            title: "Unit Rate",
            dataIndex: "unitPrice",
            // width: 100,
            render: (value, record) => {
              if (record.isStatusRow) return value;
              return (
                <InputNumber
                  min={0}
                  controls={false}
                  value={value}
                  style={{ width: "100%" }}
                  placeholder="Rate"
                  onChange={(v) => handleUnitRateChange(record.key, v)}
                />
              );
            },
          },
          {
            title: "GST %",
            dataIndex: "taxPercent",
            width: 80,
            render: (value, record) => {
              if (record.isStatusRow) return value;
              return (
                <InputNumber
                  controls={false}
                  min={0}
                  max={100}
                  value={value}
                  style={{ width: "100%" }}
                  placeholder="%"
                  onChange={(v) => handleGstPercentChange(record.key, v)}
                />
              );
            },
          },
        ]
      : []),
    {
      title: "Prev. Recvd.",
      dataIndex: "prevReceived",
      // width: 100,
      render: (value) => {
        return <div style={{ marginTop: 8 }}>{value}</div>;
      },
    },
    {
      title: "Current Recvd. Qty.",
      // width: 100,
      render: (_, record) => (
        <InputNumber
          style={{ width: "100%" }}
          controls={false}
          min={0}
          value={record.currentReceived}
          onChange={(v) => onQtyChange(v, record)}
        />
      ),
    },
    {
      title: "UoM.",
      dataIndex: "uom",
      width: 90,
      render: (value, record) => {
        if (record.isStatusRow) return value;
        return (
          <Input
            size="small"
            value={value}
            style={{ width: "90%" }}
            onChange={(e) => handleUomChange(record.key, e.target.value)}
            placeholder="UoM."
          />
        );
      },
    },
    {
      title: "Status",
      render: (_, record) => {
        const status = getStatus(record);
        if (status === "NIL") {
          return <span className="muted-text">NIL</span>;
        }
        return (
          <Tag
            color={status === "Fully Received" ? "green" : "orange"}
            style={{ marginTop: 6 }}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      align: "center",
      fixed: "right",
      render: (_, record) => {
        if (record.isStatusRow) return null;
        const isDeleting = deletingKey === record.key;
        return (
          <div style={{ marginTop: 6 }}>
            <Tooltip title="Delete Item">
              <Button
                type="text"
                danger
                size="small"
                loading={isDeleting}
                disabled={isDeleting}
                icon={<CloseOutlined style={{ color: "red" }} />}
                onClick={() => removeRow(record.key)}
                aria-label="Delete Item"
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const isEditing = (record) => record.key === editingKey;

  const startEdit = (record) => {
    setEditingKey(record.key);
  };

  const cancelEdit = () => {
    setEditingKey(null);
  };

  const saveEdit = (key) => {
    setEditingKey(null);
  };

  const calculateTotals = () => {
    const {
      subTotal: apiSubTotal,
      cgst: apiCgst,
      sgst: apiSgst,
    } = computeApiTotals();
    const apiSubTotalNum =
      apiSubTotal == null || apiSubTotal === "" ? null : Number(apiSubTotal);
    const apiCgstNum =
      apiCgst == null || apiCgst === "" ? null : Number(apiCgst);
    const apiSgstNum =
      apiSgst == null || apiSgst === "" ? null : Number(apiSgst);

    const overallTaxPercent =
      apiSubTotalNum != null &&
      Number.isFinite(apiSubTotalNum) &&
      apiSubTotalNum > 0 &&
      apiCgstNum != null &&
      apiSgstNum != null
        ? ((apiCgstNum + apiSgstNum) / apiSubTotalNum) * 100
        : null;
    const rowsWithQty = dataSource.map((row) => {
      const qty = Number(row.invoiceQty ?? 0) || 0;
      const originalQty = Number(row.originalQty ?? qty) || 0;
      const userUnitPrice = Number(row.unitPrice ?? 0) || 0;
      const baseRate = Number(row.baseRate ?? 0) || 0;
      const effectiveUnitPrice = userUnitPrice > 0 ? userUnitPrice : baseRate;
      return { row, qty, originalQty, effectiveUnitPrice };
    });

    const sumQty = rowsWithQty.reduce((s, r) => s + r.qty, 0);
    const sumOriginalQty = rowsWithQty.reduce((s, r) => s + r.originalQty, 0);
    const baseSubTotal = rowsWithQty.reduce(
      (s, r) => s + r.effectiveUnitPrice * r.qty,
      0,
    );
    const baseSubTotalOriginal = rowsWithQty.reduce(
      (s, r) => s + r.effectiveUnitPrice * r.originalQty,
      0,
    );
    const needsReconcile =
      apiSubTotalNum != null &&
      Number.isFinite(apiSubTotalNum) &&
      apiSubTotalNum > 0 &&
      Math.abs(apiSubTotalNum - baseSubTotalOriginal) > 0.5 &&
      sumOriginalQty > 0;
    const perUnitAdjustment = needsReconcile
      ? (apiSubTotalNum - baseSubTotalOriginal) / sumOriginalQty
      : 0;

    const calculatedSubTotal = rowsWithQty.reduce(
      (s, r) => s + (r.effectiveUnitPrice + perUnitAdjustment) * r.qty,
      0,
    );

    const calculatedCgst = dataSource.reduce((sum, row) => {
      const currentQty = Number(row.invoiceQty ?? 0) || 0;
      const userUnitPrice = Number(row.unitPrice ?? 0) || 0;
      const baseRate = Number(row.baseRate ?? 0) || 0;
      const effectiveUnitPrice =
        (userUnitPrice > 0 ? userUnitPrice : baseRate) + perUnitAdjustment;
      const itemSubTotal = effectiveUnitPrice * currentQty;
      const userTaxPercent =
        row.taxPercent != null ? Number(row.taxPercent) : null;
      const taxPercent =
        userTaxPercent != null && userTaxPercent > 0
          ? userTaxPercent
          : overallTaxPercent != null
            ? overallTaxPercent
            : null;
      if (taxPercent != null && taxPercent > 0) {
        const cgstPercent = taxPercent / 2; 
        return sum + (itemSubTotal * cgstPercent) / 100;
      }
      const originalQty = Number(row.originalQty ?? currentQty) || 1;
      const originalCgst =
        Number(row.originalCgstAmount ?? row.cgstAmount ?? 0) || 0;

      if (originalQty > 0 && currentQty !== originalQty) {
        return sum + originalCgst * (currentQty / originalQty);
      }
      return sum + originalCgst;
    }, 0);

    const calculatedSgst = dataSource.reduce((sum, row) => {
      const currentQty = Number(row.invoiceQty ?? 0) || 0;
      const userUnitPrice = Number(row.unitPrice ?? 0) || 0;
      const baseRate = Number(row.baseRate ?? 0) || 0;
      const effectiveUnitPrice =
        (userUnitPrice > 0 ? userUnitPrice : baseRate) + perUnitAdjustment;
      const itemSubTotal = effectiveUnitPrice * currentQty;
      const userTaxPercent =
        row.taxPercent != null ? Number(row.taxPercent) : null;
      const taxPercent =
        userTaxPercent != null && userTaxPercent > 0
          ? userTaxPercent
          : overallTaxPercent != null
            ? overallTaxPercent
            : null;

      if (taxPercent != null && taxPercent > 0) {
        const sgstPercent = taxPercent / 2; 
        return sum + (itemSubTotal * sgstPercent) / 100;
      }

      const originalQty = Number(row.originalQty ?? currentQty) || 1;
      const originalSgst =
        Number(row.originalSgstAmount ?? row.sgstAmount ?? 0) || 0;

      if (originalQty > 0 && currentQty !== originalQty) {
        return sum + originalSgst * (currentQty / originalQty);
      }
      return sum + originalSgst;
    }, 0);

    const calculatedTotal =
      calculatedSubTotal + calculatedCgst + calculatedSgst;
    return {
      calculatedSubTotal,
      calculatedCgst,
      calculatedSgst,
      calculatedTotal,
    };
  };

  const computeApiTotals = () => {
    if (!draftData) {
      return { subTotal: null, cgst: null, sgst: null, total: null };
    }

    const items = draftData?.items || draftData?.line_items || [];
    const hasHeaderTotals =
      draftData?.basic_sub_total != null ||
      draftData?.cgst != null ||
      draftData?.sgst != null ||
      draftData?.invoice_total != null ||
      draftData?.total_amount != null;

    if (hasHeaderTotals) {
      return {
        subTotal: draftData?.basic_sub_total ?? null,
        cgst: draftData?.cgst ?? null,
        sgst: draftData?.sgst ?? null,
        total: draftData?.invoice_total ?? draftData?.total_amount ?? null,
      };
    }

    if (Array.isArray(items) && items.length > 0) {
      const subTotal = items.reduce((sum, it) => {
        const qty = Number(it?.quantity ?? 0) || 0;
        const rate = Number(it?.rate ?? 0) || 0;
        const amt = Number(it?.amount ?? 0) || 0;
        return sum + (amt > 0 ? amt : rate * qty);
      }, 0);
      const cgst = items.reduce(
        (sum, it) => sum + (Number(it?.cgst_amount ?? 0) || 0),
        0,
      );
      const sgst = items.reduce(
        (sum, it) => sum + (Number(it?.sgst_amount ?? 0) || 0),
        0,
      );
      const itemTotals = items.reduce(
        (sum, it) => sum + (Number(it?.total_amount ?? 0) || 0),
        0,
      );
      const total = itemTotals > 0 ? itemTotals : subTotal + cgst + sgst;
      return { subTotal, cgst, sgst, total };
    }

    return { subTotal: null, cgst: null, sgst: null, total: null };
  };

  const buildDraftPayload = (isConfirmReceipt) => {
    const items = dataSource.map((row) => {
      const item = {
        raw_item_name: row.item || "",
        ai_mapped_name: row.aiItem || "",
        confidence_score: row.confidenceScore || 0.85,
        quantity: row.invoiceQty || 0,
        current_received_qty: row.currentReceived || 0,
        uom: row.uom || "PCS",
      };
      const unitPrice = Number(row.unitPrice ?? 0) || 0;
      const baseRate = Number(row.baseRate ?? 0) || 0;
      const unitFreight = Number(row.unitFreight ?? 0) || 0;

      if (baseRate > 0) {
        item.rate = baseRate;
        if (unitFreight > 0) {
          item.unit_freight = unitFreight;
        }
      } else if (unitPrice > 0) {
        item.rate = unitPrice;
      }
      const taxPercent = row.taxPercent != null ? Number(row.taxPercent) : null;
      if (taxPercent != null && taxPercent > 0) {
        item.tax_percent = taxPercent;
      }

      if (row.id != null && row.id !== "") {
        item.id = Number(row.id) || row.id;
      }
      return item;
    });
    const {
      calculatedSubTotal,
      calculatedCgst,
      calculatedSgst,
      calculatedTotal,
    } = calculateTotals();
    return {
      id:  draftData?.id,
      invoice_number: draftData?.invoice_number || poHeader?.docNo || "N/A",
      invoice_id: currentInvoiceId || draftData?.invoice_id || null,
      vendor_id: selectedVendorId ?? draftData?.vendor_id ?? null,
      site_id: selectedSiteId ?? draftData?.site_id ?? null,
      is_confirm_receipt: Boolean(isConfirmReceipt),
      is_challan: documentType === "challan",
      calculated_basic_sub_total: calculatedSubTotal,
      calculated_cgst: calculatedCgst,
      calculated_sgst: calculatedSgst,
      calculated_invoice_total: calculatedTotal,
      items,
      user_details: userDetails.map((u) => ({
        name: u.name || "",
        email: u.email || "",
        mobile: u.mobile || "",
      })),
    };
  };

  const handleSaveAsDraft = async () => {
    if (!dataSource || dataSource.length === 0) {
      ShowErrorMsg("No items to save");
      return;
    }

    if (!draftId) {
      ShowErrorMsg("Draft ID not found. Please upload a PDF first.");
      return;
    }

    try {
      setIsUploading(true);

      const payload = buildDraftPayload(false);
      await patchRequest(`inventory-drafts/${draftData?.id}/`, payload);
      SuccessNotificationMsg("Draft saved successfully");
      setPdfFile(null);
      setPdfFileUrl(null);
      setDataSource([]);
      setDraftId(null);
      setDraftData(null);
      setCurrentInvoiceId(null);
      setCurrentInvoicePk(null);
      setSelectedVendorId(null);
      setSelectedSiteId(null);
      setUserDetails([]);
      if (onSaveAsDraft) onSaveAsDraft();
      else navigate("/draft-goods-receipt");
    } catch (err) {
      ShowErrorMsg(err);
    } finally {
      setIsUploading(false);
    }
  };

  const executeConfirmReceipt = async () => {
    if (!dataSource || dataSource.length === 0) {
      ShowErrorMsg("No items to save");
      return;
    }
    if (!draftId) {
      ShowErrorMsg("Draft ID not found. Please upload a PDF first.");
      return;
    }
    try {
      setIsUploading(true);
      const payload = buildDraftPayload(true);
      const response = await patchRequest(
        `inventory-drafts/${draftData?.id}/`,
        payload,
      );
      SuccessNotificationMsg("Receipt confirmed successfully");
      setPdfFile(null);
      setPdfFileUrl(null);
      setDataSource([]);
      setDraftId(null);
      setDraftData(null);
      setCurrentInvoiceId(null);
      setCurrentInvoicePk(null);
      setSelectedVendorId(null);
      setSelectedSiteId(null);
      setUserDetails([]);
      if (onConfirmReceipt) {
        const isChallanDoc =
          documentType === "challan" || draftData?.is_challan === true;
        onConfirmReceipt({ documentType: isChallanDoc ? "challan" : "invoice" });
      }
      else navigate("/inventory-goods-receipt-list");
    } catch (err) {
      ShowErrorMsg(err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmReceipt = () => {
    if (selectedSiteId == null || selectedSiteId === "") {
      ShowErrorMsg("Please select a site before confirming receipt.");
      return;
    }
    Modal.confirm({
      title: "Confirm Receipt",
      content:
        "Are you sure you want to confirm this receipt? This will finalize the material receipt and update the records.",
      okText: "Yes, Confirm",
      cancelText: "Cancel",
      onOk: () => executeConfirmReceipt(),
    });
  };

  const colors = ["#094E8F"];

  return (
    <>
      <div className="FormBody">
        <div className="stepscontentwrap" style={{ padding: 0 }}>
          <div className="stepsContent">
            <div
              className="formwrapper"
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <div className="po-header-details">
                <div className="po-row">
                  <span>
                    <strong>Doc No :</strong>
                    {(pdfFile || draftId) && (
                      <span style={{ marginLeft: 4 }}>
                        {draftData?.invoice_number ?? poHeader?.docNo ?? "N/A"}
                      </span>
                    )}
                  </span>
                </div>
                <div className="po-row">
                  <strong>Supplier :</strong>
                  {(pdfFile || draftId) && (
                    <span>
                      {draftData?.vendor_name ??
                        draftData?.vendor_details?.name ??
                        "—"}
                    </span>
                  )}
                </div>

                <div className="po-row">
                  <strong>Site :</strong>
                  {(pdfFile || draftId) && (
                    <Select
                      placeholder="Select site"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      value={selectedSiteId ?? undefined}
                      onChange={setSelectedSiteId}
                      style={{ minWidth: 200 }}
                      options={siteLocationList.map((s) => ({
                        label: s.site_name || s.company_name || String(s.id),
                        value: s.id,
                      }))}
                    />
                  )}
                </div>
                {(pdfFile || pdfFileUrl || draftId) && (
                  <>
                    {(currentInvoiceId || currentInvoicePk) &&
                      documentType !== "challan" && (
                        <Button
                          type="primary"
                          className="SaveGradiantBtn"
                          size="small"
                          icon={<LinkOutlined />}
                          onClick={() =>
                            handleOpenLinkChallansModal({
                              mode: hasLinkedChallans ? "edit" : "link",
                            })
                          }
                          disabled={isUploading}
                          style={{
                            minWidth: 140,
                            marginLeft: 8,
                            padding: "15px",
                          }}
                        >
                          {hasLinkedChallans
                            ? "Edit Linked Challans"
                            : "Link Challans to Invoice"}
                        </Button>
                      )}
                  </>
                )}
              </div>
              <div className="po-content">
                <Row gutter={[15]}>
                  <Col xs={24} md={8}>
                    <div className="pdfcardwrap">
                      <h4 className="pdf-card-title">
                        Document Viewer
                      </h4>
                      <div className="pdf-card">
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            position: "absolute",
                            right: "25px",
                            zIndex: "9",
                          }}
                        >
                          {(pdfFile || pdfFileUrl) && (
                            <Button
                              danger
                              size="small"
                              icon={<CloseOutlined />}
                              onClick={handleCloseInvoice}
                              disabled={isUploading}
                            ></Button>
                          )}
                        </div>

                        {isUploading ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              minHeight: "400px",
                              flexDirection: "column",
                              gap: "16px",
                            }}
                          >
                            <Spin size="large" />
                            <p>Uploading and processing PDF...</p>
                          </div>
                        ) : pdfFile && isPdfFile(pdfFile) ? (
                          <iframe
                            title="PDF preview"
                            src={`${localPreviewUrl || ""}#toolbar=0&navpanes=0`}
                            style={{
                              width: "100%",
                              minHeight: "500px",
                              border: "none",
                            }}
                          />
                        ) : pdfFile && isImageFile(pdfFile) ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={localPreviewUrl || ""}
                              alt="Challan preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: 600,
                                objectFit: "contain",
                                borderRadius: 6,
                                border: "1px solid #eee",
                              }}
                            />
                          </div>
                        ) : pdfFileUrl ? (
                          isPdfUrl(pdfFileUrl) ? (
                            <iframe
                              title="PDF preview"
                              src={`${pdfFileUrl}#toolbar=0&navpanes=0`}
                              style={{
                                width: "100%",
                                minHeight: "500px",
                                border: "none",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                src={pdfFileUrl}
                                alt="Document preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: 600,
                                  objectFit: "contain",
                                  borderRadius: 6,
                                  border: "1px solid #eee",
                                }}
                              />
                            </div>
                          )
                        ) : (
                          <div className="pdf-placeholder">
                            <div className="pdfuploader">
                              <p>
                                Upload a PDF/Image to preview the Bill/Challan
                                <br />
                                <small>
                                  Handwritten documents are not supported.
                                </small>
                              </p>

                              <div
                                style={{
                                  display: "flex",
                                  gap: 12,
                                  justifyContent: "center",
                                  flexWrap: "wrap",
                                }}
                              >
                              <Upload
                                  accept="application/pdf,image/*"
                                  showUploadList={false}
                                  beforeUpload={(file) =>
                                    handleUpload(file, "challan")
                                  }
                                  disabled={isUploading}
                                >
                                  <Button
                                    type="primary"
                                    icon={<UploadOutlined />}
                                    className="BlueShadowBtn"
                                    loading={isUploading}
                                  >
                                    Upload Challan
                                  </Button>
                                </Upload>

<span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "0 8px",
                                    color: "#8c8c8c",
                                    fontWeight: 500,
                                    fontSize: 14,
                                  }}
                                >
                                  OR
                                </span>
                                <Upload
                                  accept="application/pdf"
                                  showUploadList={false}
                                  beforeUpload={(file) =>
                                    handleUpload(file, "invoice")
                                  }
                                  disabled={isUploading}
                                >
                                  <Button
                                    type="primary"
                                    icon={<UploadOutlined />}
                                    className="BlueShadowBtn"
                                    loading={isUploading}
                                  >
                                    Upload Invoice
                                  </Button>
                                </Upload>
                                
                                
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col
                    xs={24}
                    md={16}
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <h4
                        className="pdf-card-title"
                        style={{ marginBottom: 10 }}
                      >
                        Extracted Data & AI Mapping
                      </h4>
                      {!isUploading &&
                        (currentInvoicePk ||
                          currentInvoiceId ||
                          draftId) &&
                        (pdfFile || pdfFileUrl || draftId) && (
                        <Button
                          type="primary"
                          className="SaveGradiantBtn"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={addRow}
                          disabled={isUploading}
                          style={{ minWidth: 100, marginRight: 8 }}
                        >
                          Add item
                        </Button>
                      )}
                    </div>

                    <section
                      className="grid-sec tablegridsec aidocintaktable"
                      style={{ flex: "auto", height: "1px" }}
                    >
                      <div className="PR-body" style={{ padding: "0" }}>
                        <div className="tablewrp">
                          <Table
                            rowKey="key"
                            columns={columns}
                            dataSource={dataSource}
                            loading={isUploading}
                            // scroll={{ x: 'auto' }}
                            scroll={{ x: 1200 }}
                            pagination={false}
                            tableLayout="fixed"
                          />
                        </div>
                      </div>
                    </section>
                  </Col>
                </Row>

                <Row gutter={[15]} style={{ marginTop: 24 }}>
                  {(pdfFile || pdfFileUrl || draftId) &&
                    dataSource.length > 0 &&
                    !isChallanUpload && (
                      <>
                        <Col xs={24} md={12}>
                          <div
                            style={{
                              border: "1px solid #d9d9d9",
                              borderRadius: 4,
                              padding: 16,
                              backgroundColor: "#fafafa",
                            }}
                          >
                            <h4
                              style={{
                                marginBottom: 16,
                                fontSize: 16,
                                fontWeight: 600,
                              }}
                            >
                              Total Amount (From Invoice)
                            </h4>
                            {(() => {
                              const { subTotal, cgst, sgst, total } =
                                computeApiTotals();
                              const fmt = (n) =>
                                n != null
                                  ? `₹${Number(n).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`
                                  : "—";
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 12,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      paddingBottom: 8,
                                      borderBottom: "1px solid #e8e8e8",
                                    }}
                                  >
                                    <span style={{ fontWeight: 500 }}>
                                      Sub Total:
                                    </span>
                                    <span>{fmt(subTotal)}</span>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      paddingBottom: 8,
                                      borderBottom: "1px solid #e8e8e8",
                                    }}
                                  >
                                    <span style={{ fontWeight: 500 }}>
                                      CGST:
                                    </span>
                                    <span>{fmt(cgst)}</span>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      paddingBottom: 8,
                                      borderBottom: "1px solid #e8e8e8",
                                    }}
                                  >
                                    <span style={{ fontWeight: 500 }}>
                                      SGST:
                                    </span>
                                    <span>{fmt(sgst)}</span>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      paddingTop: 8,
                                      fontSize: 16,
                                      fontWeight: 600,
                                      borderTop: "2px solid #1890ff",
                                    }}
                                  >
                                    <span>Total Amount:</span>
                                    <span style={{ color: "#1890ff" }}>
                                      {fmt(total)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </Col>
                        <Col xs={24} md={12}>
                          <div
                            style={{
                              border: "1px solid #d9d9d9",
                              borderRadius: 4,
                              padding: 16,
                              backgroundColor: "#fafafa",
                            }}
                          >
                            <h4
                              style={{
                                marginBottom: 16,
                                fontSize: 16,
                                fontWeight: 600,
                              }}
                            >
                              Calculated Total Amount
                            </h4>
                            {(() => {
                              const { total: apiTotal } = computeApiTotals();
                              const {
                                calculatedSubTotal,
                                calculatedCgst,
                                calculatedSgst,
                                calculatedTotal,
                              } = calculateTotals();
                              const apiTotalNum =
                                apiTotal == null || apiTotal === ""
                                  ? null
                                  : Number(apiTotal);
                              const isMismatch =
                                apiTotalNum != null &&
                                Number.isFinite(apiTotalNum) &&
                                Math.abs(apiTotalNum - calculatedTotal) > 0.5;
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 12,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      paddingBottom: 8,
                                      borderBottom: "1px solid #e8e8e8",
                                    }}
                                  >
                                    <span style={{ fontWeight: 500 }}>
                                      Sub Total:
                                    </span>
                                    <span>
                                      ₹
                                      {calculatedSubTotal.toLocaleString(
                                        "en-IN",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        },
                                      )}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      paddingBottom: 8,
                                      borderBottom: "1px solid #e8e8e8",
                                    }}
                                  >
                                    <span style={{ fontWeight: 500 }}>
                                      CGST:
                                    </span>
                                    <span>
                                      ₹
                                      {calculatedCgst.toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      paddingBottom: 8,
                                      borderBottom: "1px solid #e8e8e8",
                                    }}
                                  >
                                    <span style={{ fontWeight: 500 }}>
                                      SGST:
                                    </span>
                                    <span>
                                      ₹
                                      {calculatedSgst.toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      paddingTop: 9,
                                      fontSize: 16,
                                      fontWeight: 600,
                                      borderTop: `2px solid ${isMismatch ? "#ff4d4f" : "#52c41a"}`,
                                      background: isMismatch
                                        ? "#fff1f0"
                                        : "transparent",
                                      padding: isMismatch
                                        ? "10px 8px"
                                        : undefined,
                                      borderRadius: isMismatch ? 4 : undefined,
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 6,
                                      }}
                                    >
                                      Calculated Total:
                                      {isMismatch && (
                                        <Tooltip
                                          title={
                                            apiTotalNum != null
                                              ? `Calculated total differs from API total. API Total: ₹${apiTotalNum.toLocaleString(
                                                  "en-IN",
                                                  {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  },
                                                )} • Calculated Total: ₹${calculatedTotal.toLocaleString(
                                                  "en-IN",
                                                  {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  },
                                                )}`
                                              : "Calculated total differs from API total."
                                          }
                                        >
                                          <InfoCircleOutlined
                                            style={{ color: "#ff4d4f" }}
                                          />
                                        </Tooltip>
                                      )}
                                    </span>
                                    <span
                                      style={{
                                        color: isMismatch
                                          ? "#ff4d4f"
                                          : "#52c41a",
                                        fontWeight: 700,
                                      }}
                                      title={
                                        isMismatch && apiTotalNum != null
                                          ? `Mismatch vs API Total: ₹${apiTotalNum.toLocaleString(
                                              "en-IN",
                                              {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              },
                                            )}`
                                          : ""
                                      }
                                    >
                                      ₹
                                      {calculatedTotal.toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </Col>
                      </>
                    )}

                  {(pdfFile || pdfFileUrl || draftId) &&
                    dataSource.length > 0 && (
                      <Col xs={24}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 12,
                            marginBottom: 10,
                          }}
                        >
                          <h4 className="pdf-card-title" style={{ margin: 0 }}>
                            Notification Recipients
                            <Tooltip
                              color="#094E8F"
                              title="Add one or more recipients who will receive the notification via Email"
                            >
                              <InfoCircleOutlined
                                style={{
                                  marginLeft: 6,
                                  color: "#094E8F",
                                  fontSize: 14,
                                }}
                              />
                            </Tooltip>
                          </h4>
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={addUserDetailRow}
                            disabled={isUploading}
                            style={{ minWidth: 140 }}
                          >
                            Add recipient
                          </Button>
                        </div>
                        <section
                          className="grid-sec mt-3 tablegridsec aidocintaktable"
                          style={{ minHeight: 120 }}
                        >
                          <div className="PR-body" style={{ padding: 0 }}>
                            <div className="tablewrp">
                              <Table
                                dataSource={userDetails}
                                rowKey="id"
                                size="middle"
                                pagination={false}
                                locale={{
                                  emptyText: (
                                    <div
                                      style={{
                                        padding: "24px 16px",
                                        color: "#8c8c8c",
                                        textAlign: "center",
                                      }}
                                    >
                                      No notification recipients added yet.
                                      Click &quot;Add recipient&quot; to add.
                                    </div>
                                  ),
                                }}
                                scroll={{ x: 640 }}
                                columns={[
                                  {
                                    title: "Name",
                                    dataIndex: "name",
                                    key: "name",
                                    width: 220,
                                    ellipsis: true,
                                    render: (val, row) => (
                                      <Input
                                        size="middle"
                                        value={val}
                                        placeholder="Name"
                                        onChange={(e) =>
                                          updateUserDetailRow(
                                            row.id,
                                            "name",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    ),
                                  },
                                  {
                                    title: "Email",
                                    dataIndex: "email",
                                    key: "email",
                                    width: 260,
                                    ellipsis: true,
                                    render: (val, row) => (
                                      <Input
                                        size="middle"
                                        value={val}
                                        placeholder="Email"
                                        onChange={(e) =>
                                          updateUserDetailRow(
                                            row.id,
                                            "email",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    ),
                                  },
                                  {
                                    title: "Mobile",
                                    dataIndex: "mobile",
                                    key: "mobile",
                                    width: 140,
                                    render: (val, row) => (
                                      <Input
                                        size="middle"
                                        value={val}
                                        placeholder="10 digits"
                                        maxLength={10}
                                        onChange={(e) => {
                                          const v = e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, 10);
                                          updateUserDetailRow(
                                            row.id,
                                            "mobile",
                                            v,
                                          );
                                        }}
                                      />
                                    ),
                                  },
                                  {
                                    title: "",
                                    key: "action",
                                    width: 88,
                                    align: "center",
                                    render: (_, row) => (
                                      <Space size={4}>
                                        <Tooltip title="Add recipient">
                                          <Button
                                            type="text"
                                            size="small"
                                            icon={<PlusCircleOutlined />}
                                            onClick={addUserDetailRow}
                                            disabled={isUploading}
                                            aria-label="Add recipient"
                                          />
                                        </Tooltip>
                                        <Tooltip title="Remove">
                                          <Button
                                            type="text"
                                            size="small"
                                            danger
                                            icon={<MinusCircleOutlined />}
                                            onClick={() =>
                                              removeUserDetailRow(row.id)
                                            }
                                            aria-label="Remove recipient"
                                          />
                                        </Tooltip>
                                      </Space>
                                    ),
                                  },
                                ]}
                              />
                            </div>
                          </div>
                        </section>
                      </Col>
                    )}
                </Row>
              </div>
            </div>
          </div>

          <div className="FormFooter">
            <div className="good-tge">
              <Tag
                color="blue"
                style={{
                  width: "100%",
                  padding: "5px 10px",
                  borderRadius: 4,
                }}
              >
                Status: Draft - Pending Confirmation. Notifications will be sent
                upon confirmation.
              </Tag>
            </div>
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Button
                  type="primary"
                  className="CancelGradiantBtn"
                  onClick={handleSaveAsDraft}
                  loading={isUploading}
                >
                  Save as Draft
                </Button>

                <Tooltip
                  title={
                    selectedSiteId == null || selectedSiteId === ""
                      ? "Please select a site before confirming receipt"
                      : ""
                  }
                >
                  <span>
                    <Button
                      type="primary"
                      className="SaveGradiantBtn"
                      onClick={handleConfirmReceipt}
                      loading={isUploading}
                      disabled={selectedSiteId == null || selectedSiteId === ""}
                    >
                      Confirm Receipt
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: "#094E8F",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LinkOutlined style={{ color: "#fff", fontSize: 16 }} />
            </div>
            <div style={{ lineHeight: 1.15 }}>
              <div style={{ fontWeight: 800 }}>Link Challans to Invoice</div>
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
                Select challans and confirm linking
              </div>
            </div>
          </div>
        }
        open={showLinkChallansModal}
        onCancel={handleCloseLinkChallansModal}
        wrapClassName="link-challans-modal"
        top
        width="60%"
        height="90%"
        style={{ top: 24 }}
        bodyStyle={{ padding: 0 }}
        footer={[
          <Button
            key="cancel"
            type="primary"
            className="CancelGradiantBtn"
            onClick={handleCloseLinkChallansModal}
          >
            Cancel
          </Button>,
          <Button
            key="link"
            type="primary"
            className="SaveGradiantBtn"
            onClick={handleLinkChallans}
            loading={linkingChallans}
            disabled={selectedChallanIds.length === 0}
            icon={<LinkOutlined />}
          > 
          {
            linkChallansMode === "edit" ? `Update Linked Challans (${selectedChallanIds.length})` : `Link Selected Challans (${selectedChallanIds.length})`
          } 
          </Button>,
        ]}
        destroyOnClose
      >
        <div style={{ padding: 16, maxHeight: "78vh", overflowY: "auto" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Row gutter={[12, 12]}>
              {/* Invoice Details */}
              <Col xs={24} md={16}>
                <Card
                  title={
                    <>
                      Vendor <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  size="small"
                  style={{ marginBottom: 0 }}
                  bodyStyle={{ padding: 12 }}
                >
                  <Row gutter={[16, 12]}>
                    <Col xs={24} sm={12}>
                      <Select
                        placeholder="Select vendor"
                        style={{ width: "100%" }}
                        value={linkInvoiceVendorId}
                        onChange={handleVendorChange}
                        showSearch
                        optionFilterProp="label"
                        options={supplierList.map((v) => ({
                          label: v.name || "—",
                          value: v.name || "",
                        }))}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>

              <Col xs={24} md={8}>
                <Card
                  title="Selection Summary"
                  size="small"
                  style={{ marginBottom: 0, backgroundColor: "#f0f9ff" }}
                  bodyStyle={{ padding: 12 }}
                >
                  <Row gutter={[12, 10]}>
                    <Col span={24}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          paddingBottom: 9,
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>
                          Selected challans
                        </span>
                        <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                          {selectedChallanIds.length}
                        </Tag>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            {linkInvoiceVendorId && (
              <>
                <Card
                  title="Available Challans"
                  size="small"
                  style={{ marginBottom: 0 }}
                  bodyStyle={{ padding: 0 }}
                  extra={
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 8,
                        justifyContent: "flex-end",
                        maxWidth: 820,
                      }}
                    >
                      <div
                        style={{ width: 260, minWidth: 200, flex: "1 1 220px" }}
                      >
                        <Input
                          placeholder="Search challan no."
                          value={challanSearchText}
                          onChange={(e) => setChallanSearchText(e.target.value)}
                          allowClear
                        />
                      </div>
                      <div
                        style={{ width: 260, minWidth: 220, flex: "1 1 260px" }}
                      >
                        <DatePicker.RangePicker
                          value={challanDateRange}
                          onChange={setChallanDateRange}
                          format="DD-MM-YYYY"
                          placeholder={["Start", "End"]}
                          style={{ width: "100%" }}
                        />
                      </div>

                      <Button
                        type="primary"
                        icon={
                          <Tooltip title="Reset filters">
                            <UndoOutlined style={{ fontSize: "20px" }} />
                          </Tooltip>
                        }
                        onClick={handleResetChallanFilters}
                        disabled={loadingChallans}
                        style={{
                          backgroundColor: "#094e8f",
                          color: "#ffffff",
                          border: "none",
                          width: "40px",
                          height: "40px",
                          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                  }
                >
                <div className="tablewrp">

                  <Table
                    rowKey="id"
                    dataSource={getFilteredChallans()}
                    tableLayout="fixed"
                    columns={[
                      {
                        title: (
                          <Checkbox
                            checked={
                              getFilteredChallans().length > 0 &&
                              getFilteredChallans().every((c) =>
                                selectedChallanIds.includes(c.id),
                              )
                            }
                            indeterminate={
                              selectedChallanIds.length > 0 &&
                              selectedChallanIds.length <
                                getFilteredChallans().length
                            }
                            onChange={(e) =>
                              handleSelectAllChallans(e.target.checked)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        ),
                        key: "checkbox",
                        width: 50,
                        render: (_, record) => (
                          <Checkbox
                            checked={selectedChallanIds.includes(record.id)}
                            onChange={(e) =>
                              handleChallanSelection(
                                e.target.checked,
                                record.id,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        ),
                      },
                      {
                        title: "Challan Number",
                        dataIndex: "challan_number",
                        key: "challan_number",
                        width: 150,
                        render: (val) => val || "—",
                      },
                      {
                        title: "Date",
                        key: "date",
                        width: 120,
                        render: (_, record) =>
                          formatDate(
                            record.challan_date ||
                              record.date ||
                              record.created_at,
                          ),
                      },
                      {
                        title: "Status",
                        key: "status",
                        width: 160,
                        render: (_, record) => {
                          const text =
                            record.status_display || record.status || "—";
                          return (
                            <Tooltip title={text} placement="topLeft">
                              <Tag
                                color={
                                  record.status === "Draft" ? "orange" : "blue"
                                }
                                style={{
                                  display: "inline-block",
                                  maxWidth: 170,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  verticalAlign: "bottom",
                                }}
                              >
                                {text}
                              </Tag>
                            </Tooltip>
                          );
                        },
                      },
                      {
                        title: "Items",
                        key: "total_items",
                        width: 70,
                        align: "right",
                        render: (_, record) =>
                          record.total_items != null
                            ? String(record.total_items)
                            : "—",
                      },
                      {
                        title: "Created By",
                        key: "created_by_name",
                        width: 120,
                        render: (_, record) => record.created_by_name || "—",
                      },
                      {
                        title: "Document",
                        key: "document",
                        width: 110,
                        render: (_, record) =>
                          record.document_file_url ? (
                            <a
                              href={record.document_file_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View
                            </a>
                          ) : (
                            "—"
                          ),
                      },
                    ]}
                    pagination={false}
                    scroll={{ x: 900, y: 320 }}
                    size="small"
                    bordered
                    expandable={{
                      expandedRowRender: (record) => {
                        const supplier =
                          record.supplier_address ||
                          record.supplier_gstin ||
                          record.supplier_phone ||
                          record.supplier_email;
                        const delivery =
                          record.delivery_address ||
                          record.delivery_contact_person ||
                          record.delivery_contact_phone;
                        const transport =
                          record.vehicle_number ||
                          record.driver_name ||
                          record.driver_phone ||
                          record.transport_company ||
                          record.lr_number;
                        const items = Array.isArray(record.challan_items)
                          ? record.challan_items
                          : [];

                        return (
                          <div style={{ padding: "8px 8px 0" }}>
                            <Row gutter={[16, 8]}>
                              <Col xs={24} md={8}>
                                <div
                                  style={{ fontWeight: 600, marginBottom: 6 }}
                                >
                                  Supplier
                                </div>
                                <div style={{ color: "#595959" }}>
                                  {supplier ? (
                                    <>
                                      {record.supplier_gstin ? (
                                        <div>
                                          GSTIN: {record.supplier_gstin}
                                        </div>
                                      ) : null}
                                      {record.supplier_address ? (
                                        <div>{record.supplier_address}</div>
                                      ) : null}
                                      {record.supplier_phone ? (
                                        <div>
                                          Phone: {record.supplier_phone}
                                        </div>
                                      ) : null}
                                      {record.supplier_email ? (
                                        <div>
                                          Email: {record.supplier_email}
                                        </div>
                                      ) : null}
                                    </>
                                  ) : (
                                    "—"
                                  )}
                                </div>
                              </Col>

                              <Col xs={24} md={8}>
                                <div
                                  style={{ fontWeight: 600, marginBottom: 6 }}
                                >
                                  Delivery
                                </div>
                                <div style={{ color: "#595959" }}>
                                  {delivery ? (
                                    <>
                                      {record.delivery_address ? (
                                        <div>{record.delivery_address}</div>
                                      ) : null}
                                      {record.delivery_contact_person ? (
                                        <div>
                                          Contact:{" "}
                                          {record.delivery_contact_person}
                                        </div>
                                      ) : null}
                                      {record.delivery_contact_phone ? (
                                        <div>
                                          Phone: {record.delivery_contact_phone}
                                        </div>
                                      ) : null}
                                    </>
                                  ) : (
                                    "—"
                                  )}
                                </div>
                              </Col>

                              <Col xs={24} md={8}>
                                <div
                                  style={{ fontWeight: 600, marginBottom: 6 }}
                                >
                                  Transport
                                </div>
                                <div style={{ color: "#595959" }}>
                                  {transport ? (
                                    <>
                                      {record.transport_company ? (
                                        <div>{record.transport_company}</div>
                                      ) : null}
                                      {record.vehicle_number ? (
                                        <div>
                                          Vehicle: {record.vehicle_number}
                                        </div>
                                      ) : null}
                                      {record.driver_name ? (
                                        <div>Driver: {record.driver_name}</div>
                                      ) : null}
                                      {record.driver_phone ? (
                                        <div>Phone: {record.driver_phone}</div>
                                      ) : null}
                                      {record.lr_number ? (
                                        <div>LR: {record.lr_number}</div>
                                      ) : null}
                                    </>
                                  ) : (
                                    "—"
                                  )}
                                </div>
                              </Col>
                            </Row>

                            {record.remarks ? (
                              <>
                                <Divider style={{ margin: "12px 0" }} />
                                <div
                                  style={{ fontWeight: 600, marginBottom: 6 }}
                                >
                                  Remarks
                                </div>
                                <div style={{ color: "#595959" }}>
                                  {record.remarks}
                                </div>
                              </>
                            ) : null}

                            {items.length > 0 ? (
                              <>
                                <Divider style={{ margin: "12px 0" }} />
                                <div
                                  style={{ fontWeight: 600, marginBottom: 8 }}
                                >
                                  Challan Items ({items.length})
                                </div>
                                <Table
                                  rowKey="id"
                                  size="small"
                                  dataSource={items}
                                  pagination={false}
                                  // scroll={{ x: 600 }}
                                  columns={[
                                    {
                                      title: "Item",
                                      key: "item_name",
                                      render: (_, it) =>
                                        it.item_name ||
                                        it.item_description ||
                                        "—",
                                    },
                                    {
                                      title: "Qty.",
                                      dataIndex: "quantity",
                                      key: "quantity",
                                      width: 90,
                                      align: "right",
                                      render: (v) =>
                                        v != null && v !== "" ? v : "—",
                                    },
                                    {
                                      title: "UoM.",
                                      dataIndex: "uom",
                                      key: "uom",
                                      width: 80,
                                      render: (v) => v || "—",
                                    }
                                  ]}
                                />
                              </>
                            ) : null}
                          </div>
                        );
                      },
                      rowExpandable: (record) =>
                        !!record.remarks ||
                        !!record.document_file_url ||
                        !!record.supplier_address ||
                        !!record.delivery_address ||
                        (Array.isArray(record.challan_items) &&
                          record.challan_items.length > 0),
                    }}
                    locale={{
                      emptyText: (
                        <div
                          style={{
                            padding: "24px",
                            textAlign: "center",
                            color: "#8c8c8c",
                          }}
                        >
                          {loadingChallans
                            ? "Loading challans..."
                            : "No challans available for this vendor"}
                        </div>
                      ),
                    }}
                    onRow={(record) => ({
                      onClick: () => {
                        const isSelected = selectedChallanIds.includes(
                          record.id,
                        );
                        handleChallanSelection(!isSelected, record.id);
                      },
                      style: {
                        cursor: "pointer",
                        backgroundColor: selectedChallanIds.includes(record.id)
                          ? "#e6f7ff"
                          : "transparent",
                      },
                    })}
                  />
                </div>
                </Card>
              </>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

AiDocIntakePdf.propTypes = {
  embedded: PropTypes.bool,
  editDraftId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSaveAsDraft: PropTypes.func,
  onConfirmReceipt: PropTypes.func,
};

export default AiDocIntakePdf;
